import React, { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useStartupParamsStore } from "@/store/startupParams";
import { RaffleInitResponse, RaffleParticipant } from "@/apps/raffle/types";
import { useRaffleData } from "./useRaffleData";
import { getCurrentParticipantIndex } from "./utils";

type LoadedRafflePageContextValue = {
  raffle: RaffleInitResponse;
  participants: RaffleParticipant[];
  currentParticipantIndex?: number;
  isParticipationComplete: boolean;
};

type RafflePageData = {
  isLoading: boolean;
  raffleData: LoadedRafflePageContextValue | null;
};

const RafflePageContext = createContext<LoadedRafflePageContextValue | null>(
  null
);

export const useRafflePageData = (): RafflePageData => {
  const { bonusId } = useParams();
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const { raffle, participants, isLoading } = useRaffleData(yandexId, bonusId);

  const raffleData = useMemo<LoadedRafflePageContextValue | null>(() => {
    if (!raffle) return null;

    const { conditions } = raffle.me;
    const isRaffleFinished = Boolean(raffle.drawn_at);
    const hasMetParticipationConditions = Boolean(
      conditions?.has_goal_this_week &&
        conditions?.has_completed_order_this_week
    );

    return {
      raffle,
      participants,
      currentParticipantIndex: getCurrentParticipantIndex(raffle, participants),
      isParticipationComplete:
        isRaffleFinished || hasMetParticipationConditions,
    };
  }, [participants, raffle]);

  return {
    isLoading,
    raffleData,
  };
};

type RafflePageProviderProps = {
  raffleData: LoadedRafflePageContextValue;
  children: React.ReactNode;
};

export const RafflePageProvider = ({
  raffleData,
  children,
}: RafflePageProviderProps) => (
  <RafflePageContext.Provider value={raffleData}>
    {children}
  </RafflePageContext.Provider>
);

export const useRafflePageContext = () => {
  const context = useContext(RafflePageContext);

  if (!context) {
    throw new Error("useRafflePage must be used within RafflePageProvider");
  }

  return context;
};
